import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Subscription } from 'rxjs';

import { TenantBootstrapService } from './tenant-bootstrap.service';
import { TenantConfig, TenantShellBranding } from './tenant.model';

const DEFAULT_BRANDING: TenantShellBranding = {
  displayName: 'WL NXT LEVEL',
  logoUrl: 'assets/generic-league-logo.svg',
  faviconUrl: 'assets/generic-league-logo.svg',
  heroImageUrl: '',
  primaryColor: '#1f2937',
  accentColor: '#6b7280',
  logoFilter: 'none',
};

function isSafeAssetUrl(value: string | undefined | null): value is string {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^(https?:)?\/\//i.test(raw)) return true;
  if (raw.startsWith('/')) return true;
  if (/^(assets|\/assets)\//i.test(raw)) return true;
  return false;
}

function isSafeCssColor(value: string | undefined | null): value is string {
  const raw = String(value || '').trim();
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(raw);
}

function resolveBranding(tenant: TenantConfig | null): TenantShellBranding {
  const branding = tenant?.branding || {};
  const displayName = branding.display_name || tenant?.name || DEFAULT_BRANDING.displayName;
  const logoUrl = isSafeAssetUrl(branding.logo_url) ? branding.logo_url : DEFAULT_BRANDING.logoUrl;
  const faviconUrl = isSafeAssetUrl(branding.favicon_url) ? branding.favicon_url : logoUrl;
  const heroImageUrl = String(branding.hero_image_url || '').trim();
  return {
    displayName,
    logoUrl,
    faviconUrl,
    heroImageUrl: isSafeAssetUrl(heroImageUrl) ? heroImageUrl : DEFAULT_BRANDING.heroImageUrl,
    primaryColor: isSafeCssColor(branding.primary_color) ? branding.primary_color : DEFAULT_BRANDING.primaryColor,
    accentColor: isSafeCssColor(branding.accent_color) ? branding.accent_color : DEFAULT_BRANDING.accentColor,
    logoFilter: logoUrl === DEFAULT_BRANDING.logoUrl ? DEFAULT_BRANDING.logoFilter : 'none',
  };
}

@Injectable({ providedIn: 'root' })
export class TenantBrandingService {
  private subscription: Subscription | null = null;
  private readonly brandingSubject = new BehaviorSubject<TenantShellBranding>(DEFAULT_BRANDING);

  readonly branding$ = this.brandingSubject.asObservable();

  constructor(
    private tenantBootstrap: TenantBootstrapService,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  get currentBranding(): TenantShellBranding {
    return this.brandingSubject.value;
  }

  init(): void {
    if (this.subscription) return;
    this.applyTenant(this.tenantBootstrap.currentTenant);
    this.subscription = this.tenantBootstrap.tenant$.subscribe((tenant) => this.applyTenant(tenant));
  }

  applyTenant(tenant: TenantConfig | null): void {
    const branding = resolveBranding(tenant);
    this.brandingSubject.next(branding);
    this.title.setTitle(branding.displayName);
    this.applyCssVariables(branding);
    this.applyFavicon(branding.faviconUrl);
    this.applyTimezone(tenant?.contact?.timezone);
  }

  private applyCssVariables(branding: TenantShellBranding): void {
    const style = this.document.documentElement.style;
    style.setProperty('--tenant-primary-color', branding.primaryColor);
    style.setProperty('--tenant-accent-color', branding.accentColor);
    style.setProperty('--tenant-logo-filter', branding.logoFilter);
    style.setProperty('--mat-progress-spinner-active-indicator-color', branding.primaryColor);
  }

  private applyFavicon(faviconUrl: string): void {
    const selector = 'link[rel="icon"], link[rel="shortcut icon"]';
    let link = this.document.head.querySelector<HTMLLinkElement>(selector);
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.type = 'image/png';
    link.href = faviconUrl;
  }

  private applyTimezone(timezone: string | undefined): void {
    const value = String(timezone || '').trim();
    try {
      if (value) new Intl.DateTimeFormat('en-CA', { timeZone: value }).format();
      this.document.documentElement.dataset['tenantTimezone'] = value || 'UTC';
    } catch {
      this.document.documentElement.dataset['tenantTimezone'] = 'UTC';
    }
  }
}

export const tenantBrandingDefaults = DEFAULT_BRANDING;
